import { loadEnv } from "@/config/env";

import { draftWorkers } from "./draft-workers";
import {
  warmupConfigOf,
  warmupSkipReason,
  type ConnectionHints,
  type SkipReason,
  type WarmupConfig,
} from "./policy";

/**
 * The drafting engine's warm-up (founder, 4 Nov: "loading the CAD files is taking forever"): on a signed-in page,
 * once the page has settled and the browser is idle, the worker Open Draft will use starts and fetches and compiles
 * the engine at low priority, and the Draft workspace's code is fetched, so a click on Open Draft finds both ready.
 * It runs once a tab: a no-op once the engine is warming or has started (a page that opened a drawing first), and
 * never where data is dear (`policy.ts`). Anything that fails here only leaves Open Draft to load as it always has.
 */

export interface WarmupDeps {
  readonly config: WarmupConfig;
  /** The connection as the browser describes it now, when it does. */
  readonly connection: () => ConnectionHints | undefined;
  /** Runs `task` after `ms`, at the browser's next idle moment; the returned function cancels it. */
  readonly later: (task: () => void, ms: number) => () => void;
  /** Starts the engine's worker; whether it did (false when it is warm already). */
  readonly warm: () => boolean;
  /** Fetches the Draft workspace's code. */
  readonly prefetch: () => Promise<unknown>;
}

export type WarmupOutcome = SkipReason | "warm-already" | "warming";

/** Plans the warm-up; the returned function cancels it if it has not run. `done` hears what it did. */
export function scheduleWarmup(
  deps: WarmupDeps,
  done: (outcome: WarmupOutcome) => void = () => undefined,
): () => void {
  const skip = warmupSkipReason(deps.config, deps.connection());
  if (skip !== undefined) {
    done(skip);
    return () => undefined;
  }
  return deps.later(() => {
    // The connection may have changed (Save-Data turned on) while the page settled.
    const late = warmupSkipReason(deps.config, deps.connection());
    if (late !== undefined) {
      done(late);
      return;
    }
    if (!deps.warm()) {
      done("warm-already");
      return;
    }
    deps.prefetch().catch(() => undefined);
    done("warming");
  }, deps.config.delayMs);
}

interface NavigatorWithConnection {
  readonly connection?: ConnectionHints | undefined;
}

/** After `ms`, then when the browser is idle (or at once where it has no idle callback). */
function afterIdle(task: () => void, ms: number): () => void {
  let idle: number | undefined;
  const timer = window.setTimeout(() => {
    if (typeof window.requestIdleCallback === "function")
      idle = window.requestIdleCallback(() => task());
    else task();
  }, ms);
  return () => {
    window.clearTimeout(timer);
    if (idle !== undefined) window.cancelIdleCallback(idle);
  };
}

/**
 * A dev page may switch the warm-up on or off whatever the settings say (`?draft-warmup=on|off`): the end-to-end
 * suite runs with it off, so its many pages do not each compile the engine, and its own spec switches it on. A real
 * build has no such parameter.
 */
const WARMUP_PARAM = "draft-warmup";

function configFor(search: string, dev: boolean): WarmupConfig {
  const config = warmupConfigOf(loadEnv());
  const asked = dev ? new URLSearchParams(search).get(WARMUP_PARAM) : null;
  return asked === "on" || asked === "off" ? { ...config, enabled: asked === "on" } : config;
}

/** The tab's warm-up, for a signed-in page to start as it mounts (`app/AppRoutes.tsx`). */
export function scheduleDraftWarmup(): () => void {
  return scheduleWarmup({
    config: configFor(window.location.search, import.meta.env.DEV),
    connection: () => (navigator as NavigatorWithConnection).connection,
    later: afterIdle,
    warm: () => draftWorkers.warmUp("low"),
    prefetch: () => import("../DraftWorkspace"),
  });
}
